import React, { useEffect } from 'react';
import { Form, message } from 'antd';
import {
  useUpdateEventAtOnceMutation,
  useGetEventByIdQuery,
  EventAttributes,
  EventTypeEnum,
  VirtualMeetingTypeEnum,
  namedOperations,
} from 'graphql/types';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import { ResizeImageResult, ParseGoogleAddressResult } from 'helpers';
import StepBarForm from 'components/stepBarForm';
import { Spinner, NotFound } from 'components/elements';
import moment from 'moment';
import {
  formatEventDateTimeInTimezone,
  getDefaultEventTimezone,
  parseEventDateTimeForForm,
} from 'utils/eventDateTime';
import { authorizeAndCreateGoogleMeet } from 'helpers/googleMeet';
import CoverPhotoStep from './coverPhotoStep';
import InformationStep from './informationStep';
import EventDetailsStep from './eventDetails';
import AddCategoriesStep from '../category/addCategoriesStep';
import { isGoogleMeetSetup, isVirtualEventType } from './virtualMeetingTypes';
import { RecurrenceTypeEnum, splitRecurringFormValues, resolveRecurrenceForApi } from './recurringOptions';

interface FromFields {
  title: string;
  dates: moment.Moment[];
  timezone: string;
  description: string;
  group: string;
  coverPhoto: ResizeImageResult;
  address: ParseGoogleAddressResult & { id?: string };
  eventType: EventTypeEnum;
  eventUrl?: string;
  passcode?: string;
  virtualMeetingType?: VirtualMeetingTypeEnum;
  autoRecording?: boolean;
  price: number;
  capacity: number;
  categories: string[];
  recurringPattern?: RecurrenceTypeEnum;
  repeatDayInterval?: number;
}

const steps = [
  {
    title: 'Informations',
    pageTitle: 'Update Event Details',
    validation: ['title', 'description', 'group', 'dates', 'timezone'],
    content: InformationStep,
  },
  {
    title: 'Categories',
    pageTitle: 'Modify related categories',
    validation: ['categories'],
    content: AddCategoriesStep,
  },
  { title: 'Cover Photo', pageTitle: 'Change Cover Photo', validation: ['coverPhoto'], content: CoverPhotoStep },
  {
    title: 'Extra Details',
    pageTitle: 'Update Extra Details',
    validation: ['price', 'eventType', 'address', 'virtualMeetingType', 'eventUrl', 'passcode'],
    content: EventDetailsStep,
  },
];

type MatchParams = { id: string };
type UpdateEventProps = RouteComponentProps<MatchParams>;

function UpdateEvent(props: UpdateEventProps) {
  const {
    match: {
      params: { id },
    },
  } = props;

  const [form] = Form.useForm<FromFields>();
  const [updateEvent, { loading }] = useUpdateEventAtOnceMutation({
    refetchQueries: [namedOperations.Query.GetMyAccount],
  });
  const history = useHistory();

  const { data: eventResult, loading: eventLoading } = useGetEventByIdQuery({
    variables: { id },
  });

  const onFinish = async (values: FromFields) => {
    const {
      dates,
      timezone,
      virtualMeetingType,
      eventUrl,
      passcode,
      autoRecording,
      recurringPattern,
      repeatDayInterval,
      coverPhoto,
      ...restValues
    } = values;
    const eventTimezone = timezone || getDefaultEventTimezone();
    const useAutoCreateZoom =
      isVirtualEventType(restValues.eventType) && virtualMeetingType === VirtualMeetingTypeEnum.AutoCreateZoom;
    const startTime = formatEventDateTimeInTimezone(dates[0], eventTimezone);
    const endTime = formatEventDateTimeInTimezone(dates[1], eventTimezone);

    // Runs first so Google's popup still counts as user-initiated.
    let meetingUrl = eventUrl;
    if (isGoogleMeetSetup(restValues.eventType, virtualMeetingType)) {
      try {
        const { hangoutLink } = await authorizeAndCreateGoogleMeet({
          summary: restValues.title,
          eventStartTime: startTime,
          eventEndTime: endTime,
          timezone: eventTimezone,
        });
        meetingUrl = hangoutLink;
      } catch (err) {
        if (err instanceof Error && err.message !== 'access_denied') {
          message.error(err.message || 'Failed to create the Google Meet.');
        }
        return;
      }
    }

    const { recurrenceType, repeatInterval } = resolveRecurrenceForApi(repeatDayInterval, recurringPattern);

    const attributes: EventAttributes = {
      ...restValues,
      recurrenceType,
      repeatInterval,
      virtualMeetingType,
      startTime,
      endTime,
      timezone: eventTimezone,
      ...(useAutoCreateZoom ? { autoRecording: Boolean(autoRecording) } : {}),
      ...(useAutoCreateZoom ? {} : { eventUrl: meetingUrl, passcode }),
    };

    // Only send a cover photo when the user picked a new one; otherwise the existing one is kept.
    if (coverPhoto?.blob) {
      attributes.coverPhoto = coverPhoto.blob;
    }

    try {
      const result = await updateEvent({
        variables: { input: attributes, eventId: id },
      });
      const eventId = result.data?.updateEventAtOnce?.data.id;
      if (eventId) {
        history.push(`/events/${eventId}`);
      }
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  useEffect(() => {
    if (eventResult?.event?.data) {
      const {
        title,
        description,
        startTime,
        endTime,
        timezone,
        group,
        recurrenceType,
        repeatInterval,
        coverPhotoUrl,
        categories,
        address,
        eventType,
        eventUrl,
        passcode,
        virtualMeetingType,
        autoRecording,
        price,
        capacity,
      } = eventResult.event.data.attributes;

      const { repeatDayInterval, recurringPattern } = splitRecurringFormValues(recurrenceType, repeatInterval);
      const { __typename, ...restAddress } = address?.data?.attributes || {};

      form.setFieldsValue({
        title,
        description,
        group: group?.data?.id,
        dates: [parseEventDateTimeForForm(startTime, timezone), parseEventDateTimeForForm(endTime, timezone)],
        timezone: timezone || getDefaultEventTimezone(),
        repeatDayInterval,
        recurringPattern,
        coverPhoto: { blob: null, url: coverPhotoUrl },
        categories: (categories?.data ?? []).map((category) => String(category.id)),
        ...(address?.data ? { address: restAddress as FromFields['address'] } : {}),
        eventType,
        eventUrl,
        passcode,
        virtualMeetingType,
        autoRecording: Boolean(autoRecording),
        price: price ?? 0,
        capacity: capacity ?? 0,
      });
    }
  }, [form, eventResult]);

  if (eventLoading) {
    return <Spinner size="xl" block />;
  }

  const handlePageBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push(`/events/${id}`);
  };

  const eventTitle = eventResult?.event?.data?.attributes?.title;

  return eventResult?.event ? (
    <StepBarForm<FromFields>
      pageTitle={`Update Event ${eventTitle}`}
      form={form}
      onFinish={onFinish}
      loading={loading}
      steps={steps}
      onPageBack={handlePageBack}
    />
  ) : (
    <NotFound>Event Not Found!</NotFound>
  );
}

export default UpdateEvent;
